import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { com, kotlin, Nullable } from '@eidu/form'
import { useNavigate } from 'react-router-dom'
import DraftFieldValue, {
  draftFieldValuesToFieldValues,
  fieldValueToString,
} from '../../../../domain/entity/DraftFieldValue'
import EqualityHashMap from '../../../../util/EqualityHashMap'
import { requireNotUndefinedOrNull } from '../../../../util/require'
import createEntityToCreate from '../../createEntityToCreate'
import hasFieldError from '../../../../domain/entity/hasFieldError'
import EntityRepository from '../../../../io/EntityRepository'
import { ConflictError } from '../../../../io/api/entity/data/postEntities'
import { useSnackBar } from '../../../../components/feedback/SnackBarProvider'
import EntityWithLabelAndRelated from '../../../../domain/entity/EntityWithLabelAndRelated'
import Link from '../../../../domain/entity/Link'
import getNonNullFieldsFromEntity from '../../../../util/entity/getNonNullFieldsFromEntity'
import useHasPermission from '../../../permission/useHasPermission'
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import EntityAction = com.eidu.sharedlib.entity.permission.EntityAction
import ExternalValueKey = com.eidu.sharedlib.entity.user.ExternalValueKey
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import validateFieldValues = com.eidu.sharedlib.entity.validation.validateFieldValues
import KtMap = kotlin.collections.KtMap

type UseEntityEditParams = {
  entity: EntityWithLabelAndRelated | undefined
  entityId: EntityId | undefined
  entityType: EntityType
  entityRepository: EntityRepository
}

type UseEntityEditResult = {
  fields: ReadonlyMap<FieldId, DraftFieldValue>
  updateField: (value: DraftFieldValue) => void
  submitting: boolean
  hasAttemptedSubmit: boolean
  valid: boolean
  submitEntity: () => Promise<void>
  deleteEntity: () => Promise<void>
  hasPermissions: boolean
  permissionsLoading: boolean
  registerExternalValues: (values: Map<ExternalValueKey, Nullable<string>>) => void
  registerValidation: (validate: () => boolean) => void
  registerPreSubmit: (fn: () => Promise<Link[]>) => void
  registerPostSubmit: (fn: (entityId: EntityId) => Promise<void>) => void
}

const useEntityEdit = ({
  entity,
  entityId,
  entityType,
  entityRepository,
}: UseEntityEditParams): UseEntityEditResult => {
  // We allow all users to see the create page (no entityId), the backend will validate the permissions
  const { hasPermission: hasRequiredPermissions, loading: permissionsLoading } = entityId
    ? useHasPermission(entityId, EntityAction.UpdateEntity)
    : { hasPermission: true, loading: false }

  const navigate = useNavigate()
  const { showSnackBar } = useSnackBar()

  const [submitting, setSubmitting] = useState(false)

  const [fields, setFields] = useState<ReadonlyMap<FieldId, DraftFieldValue>>(new Map())
  const updateField = (value: DraftFieldValue) => setFields((it) => new EqualityHashMap(it).set(value.fieldId, value))

  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const stateRef = useRef<{
    externalValues: Map<ExternalValueKey, Nullable<string>>
    validationFn: (() => boolean) | undefined
    preSubmitFn: (() => Promise<Link[]>) | undefined
    postSubmitFn: ((entityId: EntityId) => Promise<void>) | undefined
  }>({
    externalValues: new Map(),
    validationFn: undefined,
    preSubmitFn: undefined,
    postSubmitFn: undefined,
  })

  const registerExternalValues = useCallback((values: Map<ExternalValueKey, Nullable<string>>) => {
    values.forEach((value, key) => {
      stateRef.current.externalValues.set(key, value)
    })
  }, [])

  const registerValidation = useCallback((validate: () => boolean) => {
    stateRef.current.validationFn = validate
  }, [])

  const registerPreSubmit = useCallback((fn: () => Promise<Link[]>) => {
    stateRef.current.preSubmitFn = fn
  }, [])

  const registerPostSubmit = useCallback((fn: (entityId: EntityId) => Promise<void>) => {
    stateRef.current.postSubmitFn = fn
  }, [])

  useEffect(() => {
    if (entity) {
      setFields(getNonNullFieldsFromEntity(entity.entity))
    }
  }, [entity])

  const valid = useMemo(() => {
    if (!entityType) return false

    const fieldsValid = entityType.fields.asJsReadonlyArrayView().every((field) => {
      const fieldValue = fields.get(field.id) ?? undefined
      return !hasFieldError(field, fieldValue, new Map())
    })

    return fieldsValid
  }, [entityType, fields])

  const submitEntity = useCallback(async () => {
    const state = stateRef.current

    const validate = (): boolean => {
      if (state.validationFn) {
        return state.validationFn()
      }
      return true
    }

    const addCustomEditorEntityLinks = async (updatedLinks: Link[]) => {
      if (state.preSubmitFn) {
        const customEditorEntityLinks = await state.preSubmitFn()
        if (customEditorEntityLinks) {
          updatedLinks.push(...customEditorEntityLinks)
        }
      }
    }

    const getFieldValues = (): DraftFieldValue[] =>
      entity
        ? [...Array.from(fields.values())].filter((field) => {
            const originalValue = entity.entity.valuesByFieldId.asJsReadonlyMapView().get(field.fieldId)
            return !originalValue || fieldValueToString(originalValue) !== field.value
          })
        : [...Array.from(fields.values())]

    const createEntity = async (
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        const newEntityId = await entityRepository.createEntity(
          createEntityToCreate(entityType.id, fieldValues, updatedLinks, externalValues || new Map())
        )
        if (state.postSubmitFn) await state.postSubmitFn(newEntityId)
        showSnackBar(`Created ${localEntityType.name}`, 'success')
        navigate(-1)
      } catch (e) {
        console.error(e)
        if (e instanceof ConflictError) {
          showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
        } else {
          showSnackBar(`Failed to create ${localEntityType.name}`, 'error')
        }
      }
    }

    const modifyEntity = async (
      existingEntity: EntityWithLabelAndRelated,
      localEntityType: EntityType,
      fieldValues: readonly DraftFieldValue[],
      updatedLinks: readonly Link[],
      externalValues: Map<ExternalValueKey, Nullable<string>>
    ) => {
      try {
        await entityRepository.modifyEntity(existingEntity.entity.id, {
          valuesToUpdateByFieldId: draftFieldValuesToFieldValues(fieldValues),
          linksToUpdate: new Map(updatedLinks.map((link) => [link.key, link.value])),
          externalValuesToUpdate: externalValues,
        })
        if (state.postSubmitFn) await state.postSubmitFn(existingEntity.entity.id)
        showSnackBar(`${localEntityType.name} updated`, 'success')
        navigate(-1)
      } catch (e) {
        console.error(e)
        if (e instanceof ConflictError) {
          showSnackBar(`Entity login identifier already exists. Please check email and phone number.`, 'error')
        } else {
          showSnackBar(`Failed to update ${localEntityType.name}`, 'error')
        }
      }
    }

    setHasAttemptedSubmit(true)

    if (!validate()) return

    const allFieldValues = draftFieldValuesToFieldValues([...fields.values()])
    const nonNullFieldValues = new EqualityHashMap(
      [...allFieldValues.entries()].filter(
        (entry): entry is [FieldId, NonNullable<(typeof entry)[1]>] => entry[1] != null
      )
    )
    const getTypeId = async (id: EntityId): Promise<EntityTypeId | null> => {
      const typeIds = await entityRepository.getEntityTypeIds([id])
      return typeIds.get(id) ?? null
    }
    const validationResult = await validateFieldValues(
      KtMap.fromJsMap(nonNullFieldValues),
      entityType.fields,
      getTypeId
    )
    if (validationResult.asJsReadonlyMapView().size > 0) {
      showSnackBar('Please fix validation errors before saving.', 'error')
      return
    }

    setSubmitting(true)
    try {
      const updatedLinks: Link[] = []

      await addCustomEditorEntityLinks(updatedLinks)

      const localEntityType = requireNotUndefinedOrNull(entityType)
      const fieldValues = getFieldValues()

      if (!entity) {
        await createEntity(localEntityType, fieldValues, updatedLinks, state.externalValues)
      } else {
        await modifyEntity(entity, localEntityType, fieldValues, updatedLinks, state.externalValues)
      }
    } finally {
      setSubmitting(false)
    }
  }, [entityRepository, navigate, entityType, entity, fields, showSnackBar])

  const deleteEntity = useCallback(async () => {
    const localEntityType = requireNotUndefinedOrNull(entityType)
    try {
      await entityRepository.deleteEntity(requireNotUndefinedOrNull(entityId))
      showSnackBar(`Deleted ${localEntityType.name}`, 'success')
      navigate(`/entities/${localEntityType.id.toString()}`)
    } catch (e) {
      console.error(e)
      showSnackBar(`Failed to delete ${localEntityType.name}`, 'error')
    }
  }, [entityRepository, entityType, entityId, showSnackBar, navigate])

  return {
    fields,
    updateField,
    submitting,
    hasAttemptedSubmit,
    valid,
    submitEntity,
    deleteEntity,
    hasPermissions: hasRequiredPermissions,
    permissionsLoading: permissionsLoading,
    registerExternalValues,
    registerValidation,
    registerPreSubmit,
    registerPostSubmit,
  }
}

export default useEntityEdit
